/**
 * React binding for {@link LiveEngine}. Creates one engine per mount, wires it
 * to the real collaborators (WsClient, MicCapture + SileroVad, TtsPlayer, api),
 * and exposes its snapshot through `useSyncExternalStore` so the view re-renders
 * on state changes without owning any of the session logic itself.
 */
import { useEffect, useRef, useSyncExternalStore } from "react";
import * as api from "../api";
import { authStore } from "../store";
import { WsClient } from "../ws";
import { MicCapture } from "../audio/mic";
import { SileroVad } from "../audio/vad";
import { TtsPlayer } from "./tts";
import { LiveEngine, type LiveDemoMode, type LiveState } from "./engine";

export type UseLiveSessionOpts = {
  hasActive: boolean;
  youCode: string;
  guestCode: string;
  demoMode?: LiveDemoMode;
  onFinalTurn?: () => void;
};

export function useLiveSession(opts: UseLiveSessionOpts): { state: LiveState; engine: LiveEngine } {
  const { hasActive, youCode, guestCode, demoMode, onFinalTurn } = opts;

  // Keep the latest callback reachable without recreating the engine.
  const onFinalTurnRef = useRef(onFinalTurn);
  onFinalTurnRef.current = onFinalTurn;

  const engineRef = useRef<LiveEngine | null>(null);
  if (!engineRef.current) {
    engineRef.current = new LiveEngine(
      {
        createWs: (on) => new WsClient(on),
        createMic: (on) => new MicCapture(new SileroVad(), on),
        audio: new TtsPlayer(),
        patchMe: api.patchMe,
        demoReply: api.demoReply,
        onUser: (u) => authStore.getState().setUser(u),
        onFinalTurn: () => onFinalTurnRef.current?.(),
      },
      { youCode, guestCode, demoMode },
    );
  }
  const engine = engineRef.current;

  const state = useSyncExternalStore(engine.subscribe, engine.getState);

  useEffect(() => {
    if (!hasActive) return;
    engine.start();
    return () => engine.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActive]);

  return { state, engine };
}
